import React, { useState, useMemo, useContext, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Drawer,
  IconButton,
  Stack,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import EditIcon from "@mui/icons-material/Edit";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import SideBar from "../../Components/SideBar";
import { useTheme } from "../../hooks/useTheme";
import { createMuiThemeFromMode } from "../../theme/theme";
import { RoleContext } from "../../Contexts/RoleContext";
import { bountyApi } from "../../Components/bountyApi";
import { toast } from "react-toastify";
import styles from "./BountyBoard.module.scss";

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
};

// Map activity type to icon
function activityIcon(type) {
  switch (type) {
    case "claimed":
      return <PersonIcon />;
    case "completed":
    case "approved":
      return <CheckCircleIcon />;
    default:
      return <ScheduleIcon />;
  }
}

export default function BountyProfilePage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { role } = useContext(RoleContext);

  const [bounty, setBounty] = useState(location.state?.bounty || null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(!!id);
  const [activeTab, setActiveTab] = useState("Details");
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isManager = role === "admin" || role === "Manager";

  const fetchBounty = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await bountyApi.getById(id);
      setBounty(data);
      setActivity(Array.isArray(data.activity) ? data.activity : []);
    } catch (err) {
      toast.error(err.message || "Failed to load bounty.");
      setBounty(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    fetchBounty();
  }, [id, fetchBounty]);

  const handleBack = () => navigate("/bounties");

  const startEdit = () => {
    setEditForm({
      name: bounty.name || "",
      description: bounty.description || "",
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    try {
      const res = await bountyApi.update(bounty.id, {
        name: editForm.name,
        description: editForm.description,
      });
      if (res.bounty) setBounty((prev) => ({ ...prev, ...res.bounty }));
      setEditMode(false);
      toast.success("Bounty updated.");
    } catch (err) {
      toast.error(err.message || "Failed to update bounty.");
    }
  };

  const cancelEdit = () => setEditMode(false);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box className={styles.root}>
          <Grid container spacing={0}>
            {!isMobile && (
              <Grid item xs={12} md={2} className={`${styles.sidebarGrid} sidebar-column`}>
                <div className="sidebar-wrapper"><SideBar /></div>
              </Grid>
            )}
            <Grid item xs={12} md={isMobile ? 12 : 10} className={styles.mainContent}>
              <Paper elevation={0} className={styles.contentPaper}>
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </ThemeProvider>
    );
  }

  if (!bounty) {
    return (
      <ThemeProvider theme={theme}>
        <Box className={styles.root}>
          <Grid container spacing={0}>
            {!isMobile && (
              <Grid item xs={12} md={2} className={`${styles.sidebarGrid} sidebar-column`}>
                <div className="sidebar-wrapper"><SideBar /></div>
              </Grid>
            )}
            <Grid item xs={12} md={isMobile ? 12 : 10} className={styles.mainContent}>
              <Paper elevation={0} className={styles.contentPaper}>
                <Typography color="text.secondary">Bounty not found.</Typography>
                <Button onClick={() => navigate("/bounties")} sx={{ mt: 2 }}>Back to Bounty Board</Button>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </ThemeProvider>
    );
  }

  const name = bounty.name || bounty.title;
  const statusColor = bounty.status === "completed" ? "success" : bounty.status === "claimed" ? "primary" : "default";

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        {isMobile && (
          <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)}>
            <Box sx={{ width: 250, p: 2 }}>
              <IconButton onClick={() => setSidebarOpen(false)} sx={{ mb: 2 }}><CloseIcon /></IconButton>
              <SideBar />
            </Box>
          </Drawer>
        )}
        <Grid container spacing={0}>
          {!isMobile && (
            <Grid item xs={12} md={2} className={`${styles.sidebarGrid} sidebar-column`}>
              <div className="sidebar-wrapper"><SideBar /></div>
            </Grid>
          )}
          <Grid item xs={12} md={isMobile ? 12 : 10} className={styles.mainContent}>
            <Paper elevation={0} className={styles.contentPaper}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                {isMobile && (
                  <IconButton onClick={() => setSidebarOpen(true)} edge="start"><MenuIcon /></IconButton>
                )}
                <Button startIcon={<ArrowBackIcon />} onClick={handleBack} size="small">
                  Back
                </Button>
                <Box flex={1} />
                <Chip label={bounty.status} color={statusColor} size="small" variant="outlined" />
              </Stack>
              <Typography variant="h4" className={styles.pageTitle} sx={{ mb: 1 }}>
                {name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Bounty #{bounty.id}
              </Typography>

              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tab value="Details" label={<span><DescriptionOutlinedIcon sx={{ fontSize: 18, verticalAlign: "middle", mr: 0.5 }} /> Details</span>} />
                <Tab value="Activity" label={<span><TimelineOutlinedIcon sx={{ fontSize: 18, verticalAlign: "middle", mr: 0.5 }} /> Activity</span>} />
              </Tabs>

              {activeTab === "Details" && (
                <Box sx={{ py: 1 }}>
                  {editMode ? (
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Bounty Name"
                        value={editForm.name}
                        onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={3}
                        value={editForm.description}
                        onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                        size="small"
                      />
                      <Stack direction="row" spacing={1}>
                        <Button variant="contained" onClick={saveEdit}>Save</Button>
                        <Button variant="outlined" onClick={cancelEdit}>Cancel</Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <>
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                        {isManager && (
                          <Button startIcon={<EditIcon />} size="small" onClick={startEdit}>
                            Edit
                          </Button>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1 }}><strong>Name:</strong> {bounty.name || bounty.title}</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}><strong>Description:</strong> {bounty.description || "—"}</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Reward:</strong> ${bounty.reward_value ?? bounty.rewardAmount ?? 0} {bounty.reward_type || bounty.rewardType || "Cash"}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}><strong>Status:</strong> {bounty.status}</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Expiration:</strong>{" "}
                        {bounty.expires_at ? new Date(bounty.expires_at).toLocaleString() : "None"}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Scope:</strong> {bounty.scope_display || bounty.scope_type || "Overall"}
                      </Typography>
                      {bounty.conversions_required != null && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Conversions needed to win:</strong> {bounty.conversions_required}
                          {bounty.status === "available" && bounty.current_user_conversion_count != null && (
                            <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
                              · Your progress: {bounty.current_user_conversion_count} / {bounty.conversions_required}
                              {bounty.can_claim && (
                                <Typography component="span" color="success.main" sx={{ ml: 0.5 }}>· You can claim</Typography>
                              )}
                            </Typography>
                          )}
                          {(bounty.status === "claimed" || bounty.status === "completed") && bounty.conversion_count != null && (
                            <Typography component="span" color="primary.main" sx={{ ml: 1 }}>
                              · Claimer: {bounty.conversion_count} / {bounty.conversions_required}
                            </Typography>
                          )}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}

              {activeTab === "Activity" && (
                <List dense>
                  {activity.map((ev, idx) => (
                    <ListItem key={ev.id || idx}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: "primary.main", width: 36, height: 36 }}>
                          {activityIcon(ev.type)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={ev.label}
                        secondary={ev.at ? new Date(ev.at).toLocaleString() : null}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
}
